import * as React from 'react';
import { Hit } from '@algolia/client-search';

import { useInfiniteScroll } from '@blockworks/ui/hooks';

import AdContainer from '@/components/ads/ad-container';
import AdContainerIds from '@/components/ads/ad-container-ids';
import Breadcrumb from '@/components/display/headers/breadcrumb';
import RightRail from '@/components/layouts/right-rail';
import NewsSectionArticle from '@/components/pages/frontpage/components/news/news-section-article';
import { useAlgolia } from '@/context/algolia';
import { AlgoliaPostHit } from '@/types/algolia/algolia-post-hit';
import { ArticleType } from '@/types/article-type';
import mapAlgoliaHits from '@/utils/functions/algolia/map-algolia-hits';
import queryAlgoliaPosts from '@/utils/functions/algolia/query-algolia-posts';
import decodeEntities from '@/utils/functions/string/decode-entities';
import { Category } from '@/utils/functions/wordpress/fetch-categories';

type CategoryPageLayoutProps = {
    category: Category;
    articles: ArticleType[];
    pageLimit: number;
};

const CategoryPageLayout = (props: CategoryPageLayoutProps) => {
    const { articles, category } = props;
    const { algoliaKey, error, loading } = useAlgolia();
    const { renderScrollTarget, isLoading, entities } = useInfiniteScroll(articles, async (limit, page) => {
        if (!algoliaKey) {
            return [];
        }

        const posts = await queryAlgoliaPosts(
            '',
            limit,
            page,
            {
                filters: `taxonomies.category:${category.slug}`,
                proxy: true,
            },
            algoliaKey,
        );
        return mapAlgoliaHits(posts.hits as Array<Hit<AlgoliaPostHit>>);
    });

    if (!category) {
        return <></>;
    }

    return (
        <main className="min-w-full">
            <section className="flex flex-col lg:flex-row flex-wrap md:flex-nowrap">
                <div className="p-6 lg:p-12 border-b xl:border-none w-full lg:w-9/12 xl:w-11/12">
                    <Breadcrumb
                        items={[
                            {
                                title: 'News',
                                link: '/news',
                            },
                            {
                                title: category.name,
                                link: `/category/${category.slug}`,
                            },
                        ]}
                    />
                    <div>
                        <h1 className="self-stretch flex-grow-0 flex-shrink-0 w-full text-5xl font-semibold text-left text-[#110b29]">
                            {decodeEntities(category.name)}
                        </h1>
                        <p className="self-stretch flex-grow-0 flex-shrink-0 w-full text-xl text-left text-[#110b29]">
                            {decodeEntities(
                                category.description ||
                                    `There are a total of ${category.count} articles associated with ${category.name}.`,
                            )}
                        </p>
                    </div>
                    {!error && !loading && (
                        <div className="mt-12 grid gap-8 grid-cols-1 xs:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                            {entities.map((article, index) => (
                                <React.Fragment key={`NewsSectionArticle-${article.slug}`}>
                                    {index === 3 && (
                                        <div className="py-2 xs:hidden">
                                            <AdContainer
                                                googleAd={AdContainerIds.CategoryBetweenStory3And4300x250!}
                                                className="w-0 xl:w-[250px] xl:mt-8 text-center align-middle"
                                            />
                                        </div>
                                    )}
                                    <NewsSectionArticle article={article} />
                                </React.Fragment>
                            ))}
                        </div>
                    )}

                    {articles.length === 0 && !isLoading && (
                        <div className="w-full">
                            <p className="text-md w-96">There were no articles found for category {category.name}.</p>
                        </div>
                    )}
                    {renderScrollTarget()}
                </div>
                <RightRail adContainer={AdContainerIds.CategoryRight300x250!} showNewsletter />
            </section>
        </main>
    );
};

export default CategoryPageLayout;
